import {
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { Label, LabelValueType } from '@core/models/labels.model';
import { ICONS } from '@core/services/icons/icons.service';
import { selectLabels } from '@core/store/admin/tags-labels/tags-labels.selectors';
import { foldForSearch } from '@core/utils/search-text.utils';

import { FormBuilderBase } from '@shared/components/row-edition-form/form-builder/form-builder.model';
import { SelectComponent } from '@shared/components/select/select.component';

import { EntityAutoCompleteComponent } from '../entity-auto-complete/entity-auto-complete.component';

export interface LabelEntry {
  key: string;
  value: string;
}

@Component({
  selector: 'app-labels-map',
  imports: [FormsModule, TranslatePipe, EntityAutoCompleteComponent, SelectComponent],
  templateUrl: './labels-map.component.html',
  styles: [
    `
      :host {
        display: flex;
        width: 100%;
      }
    `
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LabelsMapComponent),
      multi: true
    }
  ]
})
export class LabelsMapComponent implements ControlValueAccessor, OnDestroy {
  private readonly store = inject(Store);
  protected readonly ICONS = ICONS;
  protected readonly LabelValueType = LabelValueType;

  id = input<FormBuilderBase['id']>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value = input<any>();
  /** Entity type of the row being edited (e.g. 'site'). Scopes the proposed labels. */
  entityType = input<string>('');

  protected readonly availableLabels = this.store.selectSignal(selectLabels);

  /** Resolve a label's metadata (valueType, referencedEntityType) by its name. */
  protected readonly labelsByName = computed(
    () => new Map(this.availableLabels().map((l) => [l.label, l]))
  );

  /**
   * Labels that may be proposed for the edited entity type: those scoped to it, plus the
   * unscoped ("All") labels. When no entity type is provided, all labels are proposable.
   * Resolution stays over the full set (labelsByName) so already-set entries still render.
   */
  private readonly proposableLabels = computed(() => {
    const current = this.entityType().toUpperCase();
    if (!current) {
      return this.availableLabels();
    }
    return this.availableLabels().filter((l) => {
      const scope = (l.entityType ?? '').toUpperCase();
      return !scope || scope === current;
    });
  });

  protected entries = signal<LabelEntry[]>([]);
  protected searchText = signal('');
  protected isDropdownOpen = signal(false);
  protected highlightedIndex = signal(-1);

  /** Proposable labels not already selected, filtered by the current search text. */
  protected readonly filteredOptions = computed(() => {
    const search = foldForSearch(this.searchText()).trim();
    const used = new Set(this.entries().map((e) => e.key));
    return this.proposableLabels().filter(
      (l) => !used.has(l.label) && foldForSearch(l.label).includes(search)
    );
  });

  private readonly referenceEl = viewChild<ElementRef<HTMLElement>>('reference');
  private readonly dropdownEl = viewChild<ElementRef<HTMLElement>>('dropdown');
  private cleanupAutoUpdate?: () => void;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onChange: (_value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Position the dropdown as a fixed-strategy overlay so it escapes the
    // clipping (`overflow-y-auto`/`overflow-hidden`) of the surrounding
    // row-edition-form scroll container and form-builder sections (issue #1179).
    effect(() => {
      const open = this.isDropdownOpen();
      const reference = this.referenceEl()?.nativeElement;
      const dropdown = this.dropdownEl()?.nativeElement;

      this.cleanupAutoUpdate?.();
      this.cleanupAutoUpdate = undefined;

      if (!open || !reference || !dropdown) {
        return;
      }

      this.cleanupAutoUpdate = autoUpdate(reference, dropdown, () => {
        computePosition(reference, dropdown, {
          strategy: 'fixed',
          placement: 'bottom-start',
          middleware: [
            offset(4),
            flip(),
            shift({ padding: 8 }),
            size({
              apply: ({ rects, elements }) => {
                elements.floating.style.width = `${rects.reference.width}px`;
              }
            })
          ]
        }).then(({ x, y }) => {
          Object.assign(dropdown.style, {
            position: 'fixed',
            left: `${x}px`,
            top: `${y}px`
          });
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.cleanupAutoUpdate?.();
  }

  valueTypeFor(key: string): LabelValueType {
    return this.labelsByName().get(key)?.valueType ?? LabelValueType.STRING;
  }

  referencedEntityTypeFor(key: string): string {
    return this.labelsByName().get(key)?.referencedEntityType ?? '';
  }

  enumVariantsFor(key: string): string[] {
    return this.labelsByName().get(key)?.enumVariants ?? [];
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchText.set(input.value);
    this.updateDropdown();
  }

  onKeyDown(event: KeyboardEvent): void {
    const options = this.filteredOptions();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (options.length > 0) {
        this.highlightedIndex.set((this.highlightedIndex() + 1) % options.length);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (options.length > 0) {
        this.highlightedIndex.set(
          this.highlightedIndex() <= 0 ? options.length - 1 : this.highlightedIndex() - 1
        );
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const idx = this.highlightedIndex();
      if (idx >= 0 && idx < options.length) {
        this.addLabel(options[idx]);
      } else if (options.length > 0) {
        this.addLabel(options[0]);
      }
    } else if (event.key === 'Backspace' && this.searchText().length === 0) {
      const current = this.entries();
      if (current.length > 0) {
        this.removeEntry(current.length - 1);
      }
    } else if (event.key === 'Escape') {
      this.isDropdownOpen.set(false);
      this.onTouched();
    }
  }

  onInputFocus(): void {
    this.updateDropdown();
  }

  onInputBlur(): void {
    setTimeout(() => {
      this.isDropdownOpen.set(false);
      this.onTouched();
    }, 200);
  }

  addLabel(label: Label): void {
    if (this.entries().some((e) => e.key === label.label)) {
      return;
    }
    const value = label.valueType === LabelValueType.BOOL ? 'false' : '';
    this.entries.set([...this.entries(), { key: label.label, value }]);
    this.searchText.set('');
    this.isDropdownOpen.set(false);
    this.highlightedIndex.set(-1);
    this.notifyChanges();
  }

  removeEntry(index: number): void {
    this.entries.set(this.entries().filter((_, i) => i !== index));
    this.notifyChanges();
    this.onTouched();
  }

  updateValue(index: number, value: string): void {
    const updated = [...this.entries()];
    if (updated[index]) {
      updated[index] = { ...updated[index], value };
      this.entries.set(updated);
      this.notifyChanges();
    }
  }

  private updateDropdown(): void {
    const options = this.filteredOptions();
    this.isDropdownOpen.set(options.length > 0);
    this.highlightedIndex.set(options.length > 0 ? 0 : -1);
  }

  private notifyChanges(): void {
    const result: Record<string, string> = {};
    for (const entry of this.entries()) {
      if (entry.key.trim()) {
        result[entry.key] = entry.value ?? '';
      }
    }
    this.onChange(Object.keys(result).length > 0 ? result : null);
  }

  writeValue(value: Record<string, string> | null): void {
    if (!value || typeof value !== 'object') {
      this.entries.set([]);
      return;
    }
    this.entries.set(
      Object.entries(value).map(([key, val]) => ({ key, value: val ?? '' }))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
